/**
 * Handles to game objects that main.ts creates but net/index.ts does not hand
 * us: the XP-orb manager, the furnace world, the TNT sim and the player's
 * stats. main.ts is owned by another agent, so rather than a wiring change each
 * grabber wraps one prototype method: the first time the game calls it we keep
 * `this` and call straight through. Nothing about the game's own behaviour
 * changes, and every grabber returns null (the caller degrades to "this object
 * does not replicate") if the shape ever moves.
 *
 * Item entities are not here: entities/itemEntities.ts keeps its own registry,
 * which needs no patching at all.
 */

import { XpOrbManager, type XpOrbView } from '../entities/xp';
import { FurnaceWorld } from '../game/furnace';
import { Inventory } from '../game/inventory';
import { TntSim } from '../world/tnt';
import { PlayerStats } from '../game/playerStats';

let xpInstance: XpOrbManager | null = null;
let furnaceInstance: FurnaceWorld | null = null;
let tntInstance: TntSim | null = null;
let statsInstance: PlayerStats | null = null;
let inventoryInstance: Inventory | null = null;
const installed = new Set<string>();
let warned = false;

function note(what: string): void {
  if (warned) return;
  warned = true;
  console.warn(`[net] ${what} moved; that part of gameplay replication is off`);
}

/** The private orb list, reached structurally (entities/xp.ts is not ours). */
interface OrbRef {
  dead?: boolean;
  id: number; value: number; count: number;
  view: XpOrbView;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  age: number;
}

function orbsOf(xp: XpOrbManager): OrbRef[] | null {
  const list = (xp as unknown as { orbs?: unknown }).orbs;
  return Array.isArray(list) ? (list as OrbRef[]) : null;
}

/** The live XpOrbManager, or null (node smoke tests, or a refactor). */
export function liveXp(): XpOrbManager | null {
  if (!installed.has('xp')) {
    installed.add('xp');
    const spawn = XpOrbManager.prototype.spawn;
    const tick = XpOrbManager.prototype.tick;
    if (typeof spawn === 'function') {
      XpOrbManager.prototype.spawn = function (this: XpOrbManager, ...a: Parameters<typeof spawn>): void {
        xpInstance = this;
        return spawn.apply(this, a);
      };
    }
    if (typeof tick === 'function') {
      XpOrbManager.prototype.tick = function (this: XpOrbManager, ...a: Parameters<typeof tick>): void {
        xpInstance = this;
        return tick.apply(this, a);
      };
    }
  }
  return xpInstance;
}

/** The orbs this manager holds right now (views() is only rebuilt on tick). */
export interface XpOrbRef { id: number; value: number; count: number; x: number; y: number; z: number; age: number }

export function xpOrbs(xp: XpOrbManager): readonly XpOrbRef[] {
  const orbs = orbsOf(xp);
  if (!orbs) { note('XpOrbManager.orbs'); return []; }
  return orbs.filter((o) => !o.dead).map((o) => ({ id: o.id, value: o.value, count: o.count, x: o.x, y: o.y, z: o.z, age: o.age }));
}

/** Force a drop out of the local orb list (the host says it is gone). */
export function removeXp(xp: XpOrbManager, id: number): boolean {
  const orbs = orbsOf(xp);
  if (!orbs) { note('XpOrbManager.orbs'); return false; }
  const i = orbs.findIndex((o) => o.view.id === id);
  if (i < 0) return false;
  orbs.splice(i, 1);
  return true;
}

/** Pull a mirrored orb back onto the host's position (it fell off after a lag). */
export function placeXp(xp: XpOrbManager, id: number, x: number, y: number, z: number): boolean {
  const orbs = orbsOf(xp);
  if (!orbs) { note('XpOrbManager.orbs'); return false; }
  const o = orbs.find((b) => b.view.id === id);
  if (!o) return false;
  o.x = o.px = x; o.y = o.py = y; o.z = o.pz = z;
  o.vx = 0; o.vy = 0; o.vz = 0;
  return true;
}

/** The live FurnaceWorld, or null. */
export function liveFurnaces(): FurnaceWorld | null {
  if (!installed.has('furnace')) {
    installed.add('furnace');
    const tick = FurnaceWorld.prototype.tick;
    if (typeof tick === 'function') {
      FurnaceWorld.prototype.tick = function (this: FurnaceWorld, ...a: Parameters<typeof tick>): void {
        furnaceInstance = this;
        return tick.apply(this, a);
      };
    }
  }
  return furnaceInstance;
}

/** The live TntSim, or null. `primed` fires for ignitions on any peer. */
export function liveTnt(primed?: (x: number, y: number, z: number) => void): TntSim | null {
  if (!installed.has('tnt')) {
    installed.add('tnt');
    // tick() runs every game tick, so the sim is known even before anything burns
    const tick = TntSim.prototype.tick;
    const prime = TntSim.prototype.prime;
    if (typeof tick === 'function') {
      TntSim.prototype.tick = function (this: TntSim, ...a: Parameters<typeof tick>): void {
        tntInstance = this;
        return tick.apply(this, a);
      };
    }
    if (typeof prime === 'function') {
      TntSim.prototype.prime = function (this: TntSim, x: number, y: number, z: number): void {
        tntInstance = this;
        const r = prime.call(this, x, y, z);
        primed?.(x, y, z);
        return r;
      };
    } else note('TntSim.prime');
  }
  return tntInstance;
}

/** The live PlayerStats, or null (so the host can tell a client it was hurt). */
export function liveStats(): PlayerStats | null {
  if (!installed.has('stats')) {
    installed.add('stats');
    const tick = PlayerStats.prototype.tick;
    if (typeof tick === 'function') {
      PlayerStats.prototype.tick = function (this: PlayerStats, ...a: Parameters<typeof tick>): void {
        statsInstance = this;
        return tick.apply(this, a);
      };
    }
    const damage = PlayerStats.prototype.damage;
    if (typeof damage === 'function') {
      PlayerStats.prototype.damage = function (this: PlayerStats, ...a: Parameters<typeof damage>): number {
        statsInstance = this;
        return damage.apply(this, a);
      };
    } else note('PlayerStats.damage');
  }
  return statsInstance;
}

/** The live Inventory, or null (a drop a remote player picked up is ours). */
export function liveInventory(): Inventory | null {
  if (!installed.has('inv')) {
    installed.add('inv');
    const changed = Inventory.prototype.changed;
    if (typeof changed === 'function') {
      Inventory.prototype.changed = function (this: Inventory, ...a: Parameters<typeof changed>): void {
        inventoryInstance = this;
        return changed.apply(this, a);
      };
    }
    const add = Inventory.prototype.add;
    if (typeof add === 'function') {
      Inventory.prototype.add = function (this: Inventory, ...a: Parameters<typeof add>): number {
        inventoryInstance = this;
        return add.apply(this, a);
      };
    } else note('Inventory.add');
  }
  return inventoryInstance;
}
